import { GSEndpoint } from "../models/endpoint";
import { BadRequestArgsError } from "../models/errors/networkError";
import { User } from "../models/user";
import { GroupScopes } from "../models/scopes";

export interface GroupI {
    groupId: string;
    groupName: string;
    userStatus: GroupScopes[];
    managers?: string[];
    viewers?: string[];
    members?: string[];
    settings?: GroupSettingsI;
}

export interface GroupSettingsI {
    usersCanView: boolean;
    openToLeave: boolean;
    openToJoin: boolean;
}

export interface GetGroupsI {
    groups: GroupI[];
    count: number;
    next?: string;
}

interface AddUserToGroupBodyI {
    role: GroupScopes;
    sourceGroup?: string;
}

interface GroupQuery {
    count: number;
    next_page?: string;
    name?: string;
}


export class GetGroups extends GSEndpoint<{}, GetGroupsI, GroupQuery> {
    next: string | undefined = undefined;

    constructor(query?: GroupQuery) {
        super("groups", "GET", [], query, undefined, []);
    }

    override async request(user: Partial<User>): Promise<GroupI[]> {
        const originalPath = this.handleNext();
        const response = (await super.makeRequest({}, user)) as GetGroupsI;
        this.path = originalPath;
        this.next = response.next?.split(this.gs_api_url)[1];
        return response.groups;
    }

    private handleNext(): string {
        const originalPath = this.path;
        if (this.next) {
            const nextPath = this.next.split(this.gs_api_url)[1];
            const urlParams = new URLSearchParams(this.next.split("?")[1]);
            urlParams.forEach((value, key) => {
                // @ts-ignore
                this.queryParams[key] = value;
            });
            this.path = nextPath;
        }
        return originalPath;
    }

    async requestRaw(user: Partial<User>, query?: GroupQuery): Promise<GetGroupsI> {
        if (!this.queryParams) this.queryParams = { count: 50 } as GroupQuery;
        
        // set path query params to object query params
        const originalPath = this.handleNext();

        // overwrite any current query params with given params
        this.queryParams = { ...this.queryParams, ...query };
        const response = (await super.makeRequest({}, user)) as GetGroupsI;

        // set path and next back
        this.path = originalPath;
        this.next = response.next?.split(this.gs_api_url)[1];
        return response;
    }

    async requestFromStart(user: Partial<User>, count?: number): Promise<GroupI[]> {
        if (count) this.queryParams = { count: count, ...this.queryParams };
        const response = (await this.makeRequest({}, user)) as GetGroupsI;
        return response.groups;
    }

    async requestStartingFromGroup(user: Partial<User>, groupId: string, count?: number): Promise<GroupI[]> {
        if (!this.queryParams) this.queryParams = { count: 50 } as GroupQuery;
        this.queryParams = count ? { ...this.queryParams, count: count, next_page: groupId } : { ...this.queryParams, next_page: groupId };
        const response = (await this.makeRequest({}, user)) as GetGroupsI;
        return response.groups;
    }
}

export class GetGroup extends GSEndpoint<{}, GroupI> {
    /**
     * Gets information for a specific group.
     *
     * Managers and viewers can see everything. If users_can_view is on, members can view
     * everything but who the other members are.
     *
     * @param group the id of the group to get
     */
    constructor(group: string) {
        super("groups/{group}", "GET", [], undefined, [group]);
    }

    override async request(user: Partial<User>): Promise<GroupI> {
        return (await super.makeRequest({}, user)) as GroupI;
    }
}

export class AddUserToGroup extends GSEndpoint<AddUserToGroupBodyI> {
    constructor(group: string, userId: string) {
        super("groups/{group}/{user}", "POST", [], undefined, [group, userId]);
    }

    override async request(body: AddUserToGroupBodyI | {}, user: Partial<User>): Promise<void> {
        if (!("role" in Object.keys(body))) {
            (body as AddUserToGroupBodyI)["role"] = GroupScopes.MEMBER;
        }
        await super.makeRequest(body as AddUserToGroupBodyI, user);
    }

    async requestWithAll(group: GroupI, userToAdd: Partial<User>, user: Partial<User>): Promise<void> {
        if (!userToAdd.id) throw new BadRequestArgsError("User ID is required to add user to group.");
        if (this.urlParams) {
            this.urlParams[0] = group.groupId;
            this.urlParams[1] = userToAdd.id;
        } else {
            this.urlParams = [group.groupId, userToAdd.id];
        }

        for (const status of group.userStatus) {
            const body = {
                role: status
            } as AddUserToGroupBodyI;

            await super.makeRequest(body, user);
        }
    }
}
